import { useState, useMemo, useEffect } from "react";

/**
 * Hook para filtro bidireccional empresa↔producto.
 *
 * Encapsula la lógica que ya estaba probada en VentasDetalle (commit ab924e7):
 * - Cascada empresa→producto: al elegir empresa, Producto muestra solo los suyos.
 * - Cascada inversa producto→empresa: al elegir producto, Empresa muestra solo las que lo tienen.
 * - Auto-completar empresa cuando el producto la determina unívocamente (1 sola empresa).
 * - Caso sinónimo: empresa con un único producto == su nombre → Producto redundante (disabled).
 * - Guards "set solo si distinto" en ambos effects para evitar loops.
 *
 * Los valores son SIEMPRE strings (nombre de empresa / nombre de producto), nunca IDs.
 *
 * @param {Array} clientesPais - clientes ya filtrados por el scope del componente (país, ejecutivo, etc.).
 *                               Debe ser una referencia estable (memoizar en el caller si se filtra inline).
 * @returns {Object} states, setters, opciones filtradas, flags de disabled y handlers de onChange.
 */
export function useEmpresaProductoFilter(clientesPais) {
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroProducto, setFiltroProducto] = useState("");
  // Trackea cuál dropdown tocó el usuario por última vez ('empresa' | 'producto' | null).
  // Solo se deshabilita el dropdown OPUESTO al que tocó → nunca ambos a la vez. Esto evita
  // el lock cuando el producto es sinónimo de la empresa (empresa única ↔ producto único).
  const [lastChanged, setLastChanged] = useState(null);

  // Productos dependientes de la empresa seleccionada. Sin empresa → todos.
  const productosDeEmpresa = useMemo(() => {
    const fuente = clientesPais.filter((cliente) => !filtroEmpresa || cliente.empresa === filtroEmpresa);
    return [...new Set(fuente.map((cliente) => cliente.producto).filter((producto) => producto))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [clientesPais, filtroEmpresa]);

  // Empresas dependientes del producto seleccionado (cascada inversa). Sin producto → todas.
  const empresasDeProducto = useMemo(() => {
    const fuente = clientesPais.filter((cliente) => !filtroProducto || cliente.producto === filtroProducto);
    return [...new Set(fuente.map((cliente) => cliente.empresa).filter((empresa) => empresa))].sort((a, b) =>
      a.localeCompare(b)
    );
  }, [clientesPais, filtroProducto]);

  // Caso sinónimo: la empresa tiene un único producto y coincide con su nombre → dropdown redundante.
  // Solo se deshabilita si el usuario tocó EMPRESA (nunca el dropdown que acaba de tocar).
  const productoDisabled =
    lastChanged === "empresa" &&
    Boolean(filtroEmpresa) &&
    productosDeEmpresa.length === 1 &&
    productosDeEmpresa[0] === filtroEmpresa;

  // El producto pertenece a una única empresa → la empresa queda determinada.
  // Solo se deshabilita si el usuario tocó PRODUCTO.
  const empresaDisabled = lastChanged === "producto" && Boolean(filtroProducto) && empresasDeProducto.length === 1;

  // Auto-completar producto cuando es sinónimo (para que el filtrado quede explícito).
  useEffect(() => {
    if (productoDisabled && filtroProducto !== productosDeEmpresa[0]) {
      setFiltroProducto(productosDeEmpresa[0]);
    }
  }, [productoDisabled, productosDeEmpresa, filtroProducto]);

  // Auto-completar empresa cuando el producto la determina unívocamente.
  useEffect(() => {
    if (empresaDisabled && filtroEmpresa !== empresasDeProducto[0]) {
      setFiltroEmpresa(empresasDeProducto[0]);
    }
  }, [empresaDisabled, empresasDeProducto, filtroEmpresa]);

  const onChangeEmpresa = (valor) => {
    setFiltroEmpresa(valor);
    setFiltroProducto("");
    setLastChanged(valor ? "empresa" : null);
  };

  const onChangeProducto = (valor) => {
    setFiltroProducto(valor);
    setFiltroEmpresa("");
    setLastChanged(valor ? "producto" : null);
  };

  return {
    filtroEmpresa,
    filtroProducto,
    setFiltroEmpresa,
    setFiltroProducto,
    productosDeEmpresa,
    empresasDeProducto,
    empresaDisabled,
    productoDisabled,
    onChangeEmpresa,
    onChangeProducto,
  };
}
